/**
 * User Management Page (Admin)
 *
 * Admin interface for managing users:
 * - View all users with pagination
 * - Search/filter users by email
 * - Adjust user credits
 * - Change user roles
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - NO localStorage
 * - Calls admin Cloud Functions (us-central1)
 * - Super_admin access only (enforced by AdminRoute wrapper)
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { adminFunctions } from '@/firebaseConfig';
import { useToast } from '@/contexts/ToastContext';
import { Button, Card, Input, Modal, LoadingSkeleton } from '@/components/ui';
import { AdminHeader } from '@/components/admin/AdminHeader';
import {
  type AdminUserListItem,
  type ListUsersRequest,
  type ListUsersResponse,
  type AdjustCreditsRequest,
  type AdjustCreditsResponse,
  type UpdateUserRoleRequest,
  type UpdateUserRoleResponse,
} from '@/types/admin.types';
import { UserRole } from '@/shared/types';
import { formatDate, formatFileSize } from '@/utils/formatting';



const USERS_PER_PAGE = 50;

export function UserManagement(): React.JSX.Element {
  const { showToast } = useToast();
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [adjustCreditsModal, setAdjustCreditsModal] = useState<{
    isOpen: boolean;
    user: AdminUserListItem | null;
  }>({ isOpen: false, user: null });
  const [changeRoleModal, setChangeRoleModal] = useState<{
    isOpen: boolean;
    user: AdminUserListItem | null;
  }>({ isOpen: false, user: null });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    user: AdminUserListItem | null;
  }>({ isOpen: false, user: null });

  // Form states
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [newRole, setNewRole] = useState<UserRole>(UserRole.USER);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Load users from backend
   */
  const loadUsers = async (): Promise<void> => {
    try {
      setLoading(true);

      const listUsersFunc = httpsCallable<ListUsersRequest, ListUsersResponse>(
        adminFunctions,
        'listUsers'
      );

      const offset = (currentPage - 1) * USERS_PER_PAGE;

      const result = await listUsersFunc({
        limit: USERS_PER_PAGE,
        offset,
      });

      // DEBUG: Log timestamp structure to verify fix
      if (result.data.users.length > 0) {
        console.log('[DEBUG] Raw user data sample:', result.data.users[0]);
        console.log('[DEBUG] createdAt structure:', result.data.users[0]?.createdAt);
        console.log('[DEBUG] createdAt type:', typeof result.data.users[0]?.createdAt);
      }

      setUsers(result.data.users);
      setTotal(result.data.total);
      setHasMore(result.data.hasMore);
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      }
      showToast('Failed to load users', 'error', undefined, true); // Persistent toast for error
      setLoading(false);
    }
  };

  /**
   * Load users on mount and when page changes
   */
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  /**
   * Filter users by search query
   */
  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * Handle adjust credits submit
   */
  const handleAdjustCredits = async (): Promise<void> => {
    if (!adjustCreditsModal.user) return;

    const amount = parseInt(creditsAmount, 10);

    // Validate input
    if (isNaN(amount) || amount === 0) {
      showToast('Please enter a valid non-zero amount', 'error');
      return;
    }

    if (!creditsReason.trim()) {
      showToast('Please provide a reason', 'error');
      return;
    }

    try {
      setSubmitting(true);

      const adjustCreditsFunc = httpsCallable<
        AdjustCreditsRequest,
        AdjustCreditsResponse
      >(adminFunctions, 'adjustUserCredits');

      const result = await adjustCreditsFunc({
        userId: adjustCreditsModal.user.uid,
        amount,
        reason: creditsReason.trim(),
      });

      showToast(
        `Credits adjusted. New balance: ${result.data.newBalance}`,
        'success'
      );

      // Reload users to reflect changes
      await loadUsers();

      // Close modal and reset form
      setAdjustCreditsModal({ isOpen: false, user: null });
      setCreditsAmount('');
      setCreditsReason('');
      setSubmitting(false);
    } catch (error) {
      console.error('Error adjusting credits:', error);
      showToast('Failed to adjust credits', 'error');
      setSubmitting(false);
    }
  };

  /**
   * Handle change role submit
   */
  const handleChangeRole = async (): Promise<void> => {
    if (!changeRoleModal.user) return;

    try {
      setSubmitting(true);

      const updateRoleFunc = httpsCallable<
        UpdateUserRoleRequest,
        UpdateUserRoleResponse
      >(adminFunctions, 'updateUserRole');

      await updateRoleFunc({
        userId: changeRoleModal.user.uid,
        newRole,
      });

      showToast('User role updated successfully', 'success');

      // Reload users to reflect changes
      await loadUsers();

      // Close modal
      setChangeRoleModal({ isOpen: false, user: null });
      setSubmitting(false);
    } catch (error) {
      console.error('Error changing role:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to change role';
      showToast(errorMessage, 'error');
      setSubmitting(false);
    }
  };

  /**
   * Handle delete user submit
   */
  const handleDeleteUser = async (): Promise<void> => {
    if (!deleteModal.user) return;
    if (deleteConfirmation !== 'DELETE') return;

    try {
      setSubmitting(true);
      const deleteFunc = httpsCallable<{ userId: string }, { success: boolean; message: string }>(
        adminFunctions,
        'deleteUser'
      );

      await deleteFunc({ userId: deleteModal.user.uid });

      showToast('User permanently deleted', 'success');

      // Reload users to reflect changes
      await loadUsers();

      // Close modal and reset form
      setDeleteModal({ isOpen: false, user: null });
      setDeleteConfirmation('');
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Open adjust credits modal
   */
  const openAdjustCreditsModal = (user: AdminUserListItem): void => {
    setAdjustCreditsModal({ isOpen: true, user });
    setCreditsAmount('');
    setCreditsReason('');
  };

  /**
   * Open change role modal
   */
  const openChangeRoleModal = (user: AdminUserListItem): void => {
    setChangeRoleModal({ isOpen: true, user });
    setNewRole(user.role);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="bg-red-500 text-white p-4 text-center font-bold text-xl">
        USER MANAGEMENT PAGE MOUNTED
      </div>
      {/* Header */}
      <AdminHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage user accounts, credits, and roles
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Users</p>
            <p className="text-2xl font-bold text-blue-600">{total}</p>
          </div>
        </div>
        {/* Search Bar */}
        <Card className="mb-6">
          <div className="p-4">
            <Input
              id="search-users"
              name="search"
              type="text"
              label="Search Users"
              placeholder="Search users by email..."
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              className="w-full max-w-md"
            />
          </div>
        </Card>

        {/* Users Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Storage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  // Loading skeletons
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-48" height="h-4" />
                      </td>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-20" height="h-4" />
                      </td>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-16" height="h-4" />
                      </td>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-24" height="h-4" />
                      </td>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-24" height="h-4" />
                      </td>
                      <td className="px-6 py-4">
                        <LoadingSkeleton variant="text" width="w-32" height="h-4" />
                      </td>
                    </tr>
                  ))
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {searchQuery ? 'No users found matching your search' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.uid} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {user.displayName || 'No name'}
                          </span>
                          <span className="text-xs text-gray-500">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === UserRole.SUPER_ADMIN
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.credits}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatFileSize(user.storageUsed)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openAdjustCreditsModal(user)}
                        >
                          Credits
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openChangeRoleModal(user)}
                        >
                          Role
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setDeleteModal({ isOpen: true, user })}
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && total > USERS_PER_PAGE && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {(currentPage - 1) * USERS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * USERS_PER_PAGE, total)} of {total} users
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </main>

      {/* Adjust Credits Modal */}
      <Modal
        isOpen={adjustCreditsModal.isOpen}
        onClose={() => setAdjustCreditsModal({ isOpen: false, user: null })}
        title="Adjust Credits"
      >
        {adjustCreditsModal.user && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">User</p>
              <p className="font-semibold">{adjustCreditsModal.user.email}</p>
              <p className="text-sm text-gray-500">
                Current balance: {adjustCreditsModal.user.credits} credits
              </p>
            </div>

            <Input
              id="credits-amount"
              name="amount"
              type="text"
              label="Amount"
              placeholder="Enter amount (positive to add, negative to subtract)"
              value={creditsAmount}
              onChange={(value) => setCreditsAmount(value)}
            />

            <Input
              id="credits-reason"
              name="reason"
              type="text"
              label="Reason"
              placeholder="e.g., Customer satisfaction, Correction"
              value={creditsReason}
              onChange={(value) => setCreditsReason(value)}
            />

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setAdjustCreditsModal({ isOpen: false, user: null })}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleAdjustCredits} disabled={submitting}>
                {submitting ? 'Adjusting...' : 'Adjust Credits'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Change Role Modal */}
      <Modal
        isOpen={changeRoleModal.isOpen}
        onClose={() => setChangeRoleModal({ isOpen: false, user: null })}
        title="Change User Role"
      >
        {changeRoleModal.user && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">User</p>
              <p className="font-semibold">{changeRoleModal.user.email}</p>
              <p className="text-sm text-gray-500">
                Current role: {changeRoleModal.user.role}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={UserRole.USER}>USER</option>
                <option value={UserRole.SUPER_ADMIN}>SUPER_ADMIN</option>
              </select>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ Warning: Changing a user's role will immediately affect their
                access permissions.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setChangeRoleModal({ isOpen: false, user: null })}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleChangeRole} disabled={submitting}>
                {submitting ? 'Changing...' : 'Change Role'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        title="DANGER: Delete User"
      >
        {deleteModal.user && (
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-700 font-bold">Warning: This action is irreversible.</p>
              <p className="text-sm text-red-600 mt-1">
                This will permanently delete the user <strong>{deleteModal.user.email}</strong>,
                including all their credits, order history, and stored files.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type "DELETE" to confirm
              </label>
              <Input
                id="delete-confirmation"
                name="deleteConfirmation"
                type="text"
                label="Confirmation"
                value={deleteConfirmation}
                onChange={(value) => setDeleteConfirmation(value)}
                placeholder="DELETE"
                className="mt-1"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setDeleteModal({ isOpen: false, user: null })}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={deleteConfirmation !== 'DELETE' || submitting}
                onClick={handleDeleteUser}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {submitting ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
